import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { bibleState } from '@/state/bible-state';
import { ChatCommandMenu, type ChatCommand } from './chat-command-menu';
import { COMMANDS, HELP_TEXT } from './chat-commands';

export const ChatInput = observer(function ChatInput() {
  const [inputValue, setInputValue] = useState('');
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [savedDraft, setSavedDraft] = useState('');
  const [showCommands, setShowCommands] = useState(false);
  const [commandFilter, setCommandFilter] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isLoading = bibleState.isChatLoading;

  // Focus textarea when chat panel opens
  useEffect(() => {
    if (bibleState.chatPanelVisible) {
      // Delay slightly so the panel enter animation doesn't steal focus
      const timer = setTimeout(() => textareaRef.current?.focus(), 450);
      return () => clearTimeout(timer);
    }
  }, [bibleState.chatPanelVisible]);

  // Refocus textarea when loading finishes (disabled → enabled transition)
  useEffect(() => {
    if (!isLoading) textareaRef.current?.focus();
  }, [isLoading]);

  // Derive prompt history from past user messages (newest first)
  const promptHistory = useMemo(
    () =>
      bibleState.chatMessages
        .filter((m) => m.role === 'user')
        .map((m) => m.content)
        .reverse(),
    [bibleState.chatMessages.length],
  );

  const executeCommand = useCallback(
    (input: string) => {
      const parts = input.slice(1).split(/\s+/);
      const cmd = parts[0]?.toLowerCase();
      const arg = parts.slice(1).join(' ').trim();

      switch (cmd) {
        case 'new':
          bibleState.startNewChatConversation();
          return true;
        case 'history':
          bibleState.toggleConversationList();
          return true;
        case 'clear':
          bibleState.clearChatHistory();
          return true;
        case 'ping':
          bibleState.pingPipeline();
          return true;
        case 'help':
          // Insert help as a synthetic assistant message
          bibleState.sendChatMessage('/help');
          return true;
        case 'verse':
          if (arg) {
            bibleState.sendChatMessage(`Navigate to ${arg}`);
          }
          return true;
        case 'strongs':
          if (arg) {
            bibleState.sendChatMessage(`What is Strong's number ${arg}?`);
          }
          return true;
        default:
          return false;
      }
    },
    [],
  );

  const handleSend = () => {
    const trimmed = inputValue.trim();
    if (!trimmed || isLoading) return;

    // Handle slash commands
    if (trimmed.startsWith('/')) {
      const handled = executeCommand(trimmed);
      if (handled) {
        setInputValue('');
        setHistoryIndex(-1);
        setShowCommands(false);
        textareaRef.current?.focus();
        return;
      }
    }

    bibleState.sendChatMessage(trimmed);
    setInputValue('');
    setHistoryIndex(-1);
    setShowCommands(false);
    textareaRef.current?.focus();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setInputValue(val);
    setHistoryIndex(-1);

    // Show command menu when typing '/' at start
    if (val.startsWith('/')) {
      setShowCommands(true);
      setCommandFilter(val.slice(1).split(/\s/)[0] || '');
    } else {
      setShowCommands(false);
    }
  };

  const handleCommandSelect = (cmd: ChatCommand) => {
    setInputValue(`/${cmd.name}${cmd.hasArg ? ' ' : ''}`);
    setShowCommands(false);
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Command menu navigation
    if (showCommands && (e.key === 'Escape' || e.key === 'Tab')) {
      e.preventDefault();
      setShowCommands(false);
      return;
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
      return;
    }

    // Arrow up — recall older prompt
    if (e.key === 'ArrowUp' && !showCommands) {
      const textarea = e.target as HTMLTextAreaElement;
      // Only activate when cursor is at the start or input is empty
      if (textarea.selectionStart !== 0 && inputValue.length > 0) return;
      if (promptHistory.length === 0) return;

      e.preventDefault();
      const newIndex = historyIndex + 1;
      if (newIndex >= promptHistory.length) return;

      if (historyIndex === -1) setSavedDraft(inputValue);
      setHistoryIndex(newIndex);
      setInputValue(promptHistory[newIndex]);
    }

    // Arrow down — recall newer prompt or return to draft
    if (e.key === 'ArrowDown' && !showCommands) {
      if (historyIndex < 0) return;

      e.preventDefault();
      const newIndex = historyIndex - 1;
      if (newIndex < 0) {
        setHistoryIndex(-1);
        setInputValue(savedDraft);
      } else {
        setHistoryIndex(newIndex);
        setInputValue(promptHistory[newIndex]);
      }
    }
  };

  return (
    <div className="chat-input-area">
      {showCommands && (
        <ChatCommandMenu
          commands={COMMANDS}
          filter={commandFilter}
          onSelect={handleCommandSelect}
          helpText={HELP_TEXT}
        />
      )}
      <textarea
        ref={textareaRef}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder='Ask "Where in the Bible does it talk about..." (/ for commands)'
        className="chat-input-field"
        rows={3}
        disabled={isLoading}
        data-testid="chat-input"
      />
      <button
        onClick={handleSend}
        disabled={isLoading || !inputValue.trim()}
        className="chat-send-btn"
        data-testid="chat-send"
      >
        Send
      </button>
    </div>
  );
});
